/**
 * verifyClearance — state machine for the PersonDialog Verify tab.
 *
 * Bill 2026-05-21 Person Modal Spec-Lock §7.4.2 + §7.5 + canonical-walkthrough
 * SPEC Addendum 5.2. Each Verify-tab section (LN / OFAC / IdScan / Address /
 * Phone / Email) carries its own canonical state. The rolled-up Verify pill
 * + VerifyRollupBanner derive from the per-section vector.
 *
 * Pure TypeScript — no React imports. Unit-testable; consumers wire it via
 * `useEmitEvent` for the SessionEvent fan-out (compliance category).
 *
 *   - `pass`               — automated clearance succeeded (LN green / OFAC
 *                            clear / etc.).
 *   - `pending`            — scan in flight, queued, or unrun.
 *   - `clear-via-comp-doc` — automated check produced a warning that a
 *                            collected compensating doc remediated. Doc upload
 *                            slot DRIVES the clearance — no extra FSR click.
 *   - `fsr-override`       — automated check failed; FSR explicitly overrode
 *                            with a reason text + comp doc. Locked-down audit
 *                            row emitted. Risk Mgmt notified (Adaptive Card),
 *                            cart NOT hard-blocked.
 *   - `fail`               — terminal block, no override applied.
 *
 * Audit events emitted on transitions (locked, see §7.4.2):
 *
 *   - `verify.section.cleared-via-comp-doc`  payload: { section, compDocId }
 *   - `verify.section.fsr-override`          payload: { section, reason,
 *                                                       compDocId,
 *                                                       fsrOfficerCode,
 *                                                       fsrEntraOid }
 *
 * OFAC State C (§7.5) emits an ADDITIONAL specific event in tandem with the
 * generic `verify.section.fsr-override` so Risk Management dashboards can
 * subscribe to OFAC overrides without filtering the cross-section stream:
 *
 *   - `ofac.state-c.override`                payload: { reason, compDocId,
 *                                                       fsrOfficerCode,
 *                                                       fsrEntraOid }
 *
 * Section enum mirrors the six Verify-tab panels per SPEC Addendum 5.2 table.
 * `idScan` covers the LN Driver-License-mismatch warning category — clearable
 * only by primary-ID re-capture in the CIP tab (NOT a third-party doc), so the
 * transition fn rejects `clear-via-comp-doc` for `idScan`.
 */

// ---------------------------------------------------------------------------
// State + section enums
// ---------------------------------------------------------------------------

/** Canonical per-section clearance state. Persisted on `person.extra.verify*`. */
import { napNasMatches } from './lnDisplaySettings';

export type VerifyState =
  | 'pass'
  | 'pending'
  | 'clear-via-comp-doc'
  | 'fsr-override'
  | 'fail';

/**
 * Verify-tab section identifiers. The six panels currently rendered on the
 * Verify tab per SPEC Screen 18 + Addendum 5.2.
 *
 *   - `ln`       — LexisNexis Identity / FraudPoint surface
 *   - `ofac`     — OFAC sanctions screen (3-state: A clear / B archived / C hit-current)
 *   - `idScan`   — primary ID capture in CIP tab; LN "Driver License mismatch"
 *                  reads this section
 *   - `address`  — USPS verify + LN address-mismatch warning
 *   - `phone`    — LN phone-mismatch warning
 *   - `email`    — LN email-mismatch warning
 */
export type VerifySection =
  | 'ln'
  | 'ofac'
  | 'idScan'
  | 'address'
  | 'phone'
  | 'email';

/**
 * Per-section clearance record. Authoritative shape persisted on
 * `person.extra.verifyClearance[section]`.
 *
 * `clearedAt` is ISO-8601. `compDocId` references a doc-slot upload's
 * `spItemId` (or `docCode` for in-memory CIP-scan captures that haven't
 * landed in SharePoint yet — backend reconciles on commit).
 */
export interface SectionClearance {
  readonly section: VerifySection;
  readonly state: VerifyState;
  /** Comp-doc that satisfied the gap; required for `clear-via-comp-doc` and `fsr-override`. */
  readonly compDocId?: string;
  /** Free-text FSR reason. Required for `fsr-override`. */
  readonly reason?: string;
  /** FSR officer short code captured at the time of override. */
  readonly fsrOfficerCode?: string;
  /** FSR Entra UPN (proxy for OID since AuthUser does not expose `.oid`). */
  readonly fsrEntraOid?: string;
  /** ISO-8601 timestamp of the transition. */
  readonly clearedAt?: string;
}

/** Audit event payload — fanned out via `useEmitEvent` from consumer hooks. */
export interface VerifyAuditEvent {
  readonly eventType:
    | 'verify.section.cleared-via-comp-doc'
    | 'verify.section.fsr-override'
    | 'ofac.state-c.override';
  readonly payload: Readonly<Record<string, unknown>>;
}

// ---------------------------------------------------------------------------
// Constructors / transitions
// ---------------------------------------------------------------------------

function nowIso(): string {
  return new Date().toISOString();
}

/** Initial section state — automated check has not yet completed. */
export function markPending(section: VerifySection): SectionClearance {
  return { section, state: 'pending', clearedAt: nowIso() };
}

/**
 * Contract C4 (BR side) — a MAJOR cross-document conflict (passport/ID numbers
 * not matching, DOB conflicts, major name differences: anything that may
 * indicate a non-genuine ID) contributes a hard `fail` to the `idScan` Verify
 * section + drives the red ALERT banner. `minor` conflicts are BL's
 * FieldComparisonDialog "pick" and do NOT fail idScan. `info` is ignored.
 *
 * Accepts a minimal `{ severity }` shape (not the full `ConflictFinding`) so
 * this pure module stays decoupled from the capture layer's types.
 */
export function idScanFailFromConflicts(
  findings: ReadonlyArray<{ severity: 'minor' | 'major' | 'info' }>,
): SectionClearance | null {
  return findings.some((f) => f.severity === 'major') ? markFail('idScan') : null;
}

/** Automated check passed (LN green / OFAC clear / USPS DPV-Y / etc.). */
export function markPass(section: VerifySection): SectionClearance {
  return { section, state: 'pass', clearedAt: nowIso() };
}

/** Automated check failed terminally. No override applied (yet). */
export function markFail(section: VerifySection): SectionClearance {
  return { section, state: 'fail', clearedAt: nowIso() };
}

/**
 * (Bill 2026-06-03) — derive address / phone / email Verify clearances from a
 * LexisNexis report's verification-detail rows. The smoke test's blocker: the LN
 * report CONFIRMED address + phone ("matched"), yet the Verify tab still showed
 * "Address: unrun / Phone: unrun", so the CIF never resolved. After importing the
 * report we map each contact field's match status into the clearance map:
 *   matched          → pass   (LN verified it — satisfies the section)
 *   mismatched / na  → pending (unverified — surfaced for FSR review, NOT a hard fail)
 *
 * `extractId` is recorded as the comp-doc reference for a matched address so the
 * address-proof requirement (DL-addr ≠ app-addr) is satisfiable by LN per
 * Addendum 5.2's three clear-paths (doc upload | LN verify | FSR override).
 *
 * Pure: accepts the minimal `{ fieldName, matchStatus }` shape so this module
 * stays decoupled from api-client types.
 */
export function deriveLnContactClearance(
  verifDetails: ReadonlyArray<{ fieldName?: string | null; matchStatus?: string | null }>,
  extractId?: string,
  summary?: { nap?: string | number | null; nas?: string | number | null },
): VerifyClearanceMap {
  const firstFor = (pred: (f: string) => boolean): string | null => {
    for (const row of verifDetails) {
      const fn = (row.fieldName ?? '').toLowerCase();
      if (fn && pred(fn)) return (row.matchStatus ?? 'na').toLowerCase();
    }
    return null;
  };
  let addr = firstFor((f) => f.includes('address') || f.includes('street') || f.includes('city'));
  let phone = firstFor((f) => f.includes('phone'));
  const email = firstFor((f) => f.includes('email'));
  // The DDP API returns no per-field rows - only InstantID's verification summary codes.
  // Use them (Bill 2026-10-06: Address / Phone stayed "unrun" after a DDP run).
  if (summary && (addr === null || phone === null)) {
    const m = napNasMatches(summary.nap, summary.nas);
    if (addr === null && m.address !== null) addr = m.address ? 'matched' : 'not-matched';
    if (phone === null && m.phone !== null) phone = m.phone ? 'matched' : 'not-matched';
  }

  const out: VerifyClearanceMap = {};
  if (addr) {
    out.address = addr === 'matched'
      ? (extractId ? clearViaCompDoc('address', extractId) : markPass('address'))
      : markPending('address');
  }
  if (phone) out.phone = phone === 'matched' ? markPass('phone') : markPending('phone');
  if (email) out.email = email === 'matched' ? markPass('email') : markPending('email');
  return out;
}

/**
 * Auto-clear via a collected compensating document. Per Addendum 5.2 the doc
 * upload slot DRIVES the clearance — server cross-references the uploaded
 * slot type against the active LN warning category. `idScan` is excluded
 * (DL-mismatch is only clearable by primary-ID re-capture, not third-party
 * doc) — caller should re-issue `markPass` after CIP rescan instead.
 *
 * Throws on `idScan` to make the policy violation a build-time error rather
 * than a silent allow.
 */
export function clearViaCompDoc(
  section: VerifySection,
  compDocId: string,
): SectionClearance {
  if (section === 'idScan') {
    throw new Error(
      'verifyClearance: idScan is not clearable by comp doc — re-capture primary ID in CIP tab instead (SPEC Addendum 5.2).',
    );
  }
  if (!compDocId) {
    throw new Error('verifyClearance: clearViaCompDoc requires a compDocId.');
  }
  return {
    section,
    state: 'clear-via-comp-doc',
    compDocId,
    clearedAt: nowIso(),
  };
}

/**
 * FSR-initiated override with documented reason + compensating doc. Used when
 * the section's automated check produced a hard fail (e.g. LN High-risk
 * FraudPoint score, OFAC State C hit-current) but the FSR has reviewed and
 * documented a non-fraud rationale.
 *
 * `officer.entraOid` may be empty when the auth context only exposes `upn`;
 * we still record the UPN as a lookup key. Reason text is the source of
 * truth in the audit row.
 */
export function fsrOverride(
  section: VerifySection,
  reason: string,
  compDocId: string,
  officer: { officerCode?: string; entraOid?: string },
): SectionClearance {
  if (!reason || reason.trim().length === 0) {
    throw new Error('verifyClearance: fsrOverride requires a non-empty reason.');
  }
  if (!compDocId) {
    throw new Error('verifyClearance: fsrOverride requires a compDocId.');
  }
  return {
    section,
    state: 'fsr-override',
    reason: reason.trim(),
    compDocId,
    ...(officer.officerCode ? { fsrOfficerCode: officer.officerCode } : {}),
    ...(officer.entraOid ? { fsrEntraOid: officer.entraOid } : {}),
    clearedAt: nowIso(),
  };
}

// ---------------------------------------------------------------------------
// Audit-event builders (locked names — see §7.4.2 / §7.5)
// ---------------------------------------------------------------------------

/**
 * Build the locked-name audit event payload for a transition. Returns null
 * for states that don't carry an audit row (`pass` / `pending` / `fail` are
 * derived from their underlying source signals).
 *
 * Consumers call this then pass the result to `useEmitEvent` with category
 * `'compliance'` and severity `'info'` (overrides) / `'warn'` (fail).
 */
export function auditEventForClearance(
  clearance: SectionClearance,
): VerifyAuditEvent | null {
  if (clearance.state === 'clear-via-comp-doc') {
    return {
      eventType: 'verify.section.cleared-via-comp-doc',
      payload: {
        section: clearance.section,
        compDocId: clearance.compDocId ?? '',
      },
    };
  }
  if (clearance.state === 'fsr-override') {
    return {
      eventType: 'verify.section.fsr-override',
      payload: {
        section: clearance.section,
        reason: clearance.reason ?? '',
        compDocId: clearance.compDocId ?? '',
        fsrOfficerCode: clearance.fsrOfficerCode ?? '',
        fsrEntraOid: clearance.fsrEntraOid ?? '',
      },
    };
  }
  return null;
}

/**
 * Companion audit row for the OFAC State C override path (§7.5). Emitted
 * IN ADDITION to the generic `verify.section.fsr-override` so Risk Mgmt
 * dashboards can subscribe to OFAC overrides directly without filtering the
 * cross-section stream.
 *
 * Returns null unless the clearance is an OFAC override.
 */
export function ofacStateCOverrideEvent(
  clearance: SectionClearance,
): VerifyAuditEvent | null {
  if (clearance.section !== 'ofac' || clearance.state !== 'fsr-override') {
    return null;
  }
  return {
    eventType: 'ofac.state-c.override',
    payload: {
      reason: clearance.reason ?? '',
      compDocId: clearance.compDocId ?? '',
      fsrOfficerCode: clearance.fsrOfficerCode ?? '',
      fsrEntraOid: clearance.fsrEntraOid ?? '',
    },
  };
}

// ---------------------------------------------------------------------------
// Rollup
// ---------------------------------------------------------------------------

/**
 * Worst-state-wins rollup across the section vector. Ordering — worst first:
 *
 *   fail > pending > fsr-override > clear-via-comp-doc > pass
 *
 * Rationale: `fail` is the only terminal block at the cart-submit gate;
 * `pending` is an unfinished check (yellow); `fsr-override` is yellow per
 * SPEC ("Overridden by [code]"); `clear-via-comp-doc` is green-equivalent
 * because the doc remediated the warning; `pass` is plain green.
 *
 * An empty vector returns `pending` — defensive default before any section
 * has reported.
 */
const STATE_SEVERITY: Record<VerifyState, number> = {
  fail: 4,
  pending: 3,
  'fsr-override': 2,
  'clear-via-comp-doc': 1,
  pass: 0,
};

export interface VerifyRollup {
  /** Worst state across the section vector. */
  readonly state: VerifyState;
  /** True when ≥1 section landed in `fsr-override` (drives banner caption). */
  readonly hasOverride: boolean;
  /** True when ≥1 section is `clear-via-comp-doc` (informational pill). */
  readonly hasCompDocClearance: boolean;
  /** Section that drove the rollup state (worst). Useful for banner copy. */
  readonly drivenBy?: VerifySection;
  /** Per-section clearance vector, copied for caller convenience. */
  readonly sections: ReadonlyArray<SectionClearance>;
}

export function rollupVerifyState(
  clearances: ReadonlyArray<SectionClearance>,
): VerifyRollup {
  if (clearances.length === 0) {
    return {
      state: 'pending',
      hasOverride: false,
      hasCompDocClearance: false,
      sections: [],
    };
  }
  let worst: SectionClearance = clearances[0]!;
  for (const c of clearances) {
    if (STATE_SEVERITY[c.state] > STATE_SEVERITY[worst.state]) {
      worst = c;
    }
  }
  return {
    state: worst.state,
    hasOverride: clearances.some((c) => c.state === 'fsr-override'),
    hasCompDocClearance: clearances.some((c) => c.state === 'clear-via-comp-doc'),
    drivenBy: worst.section,
    sections: clearances.slice(),
  };
}

// ---------------------------------------------------------------------------
// Presentation helpers (color/copy used by VerifyRollupBanner)
// ---------------------------------------------------------------------------

export type VerifyTone = 'ok' | 'info' | 'warn' | 'err';

/**
 * Banner tone for a given state. `clear-via-comp-doc` and `pass` are both
 * "ok" tone because the customer is clear to proceed; the banner text
 * surfaces the comp-doc distinction so the FSR sees what remediated.
 */
export function toneForState(state: VerifyState): VerifyTone {
  switch (state) {
    case 'pass': return 'ok';
    case 'clear-via-comp-doc': return 'ok';
    case 'pending': return 'info';
    case 'fsr-override': return 'warn';
    case 'fail': return 'err';
  }
}

/** Human-readable section label used in banner subtitle + status pill. */
export function labelForSection(section: VerifySection): string {
  switch (section) {
    case 'ln': return 'LexisNexis';
    case 'ofac': return 'OFAC';
    case 'idScan': return 'ID scan';
    case 'address': return 'Address';
    case 'phone': return 'Phone';
    case 'email': return 'Email';
  }
}

/**
 * Short banner caption per state. The override case interpolates the
 * FSR officer code if available (per SPEC: `"Overridden by [code]"`).
 */
export function captionForState(
  rollup: VerifyRollup,
): string {
  switch (rollup.state) {
    case 'pass':
      return rollup.hasCompDocClearance
        ? 'Cleared (one section cleared with compensating document)'
        : 'All verify checks cleared';
    case 'clear-via-comp-doc':
      return 'Cleared with compensating document';
    case 'pending':
      return 'Verify in progress';
    case 'fsr-override': {
      const overrider = rollup.sections.find((s) => s.state === 'fsr-override');
      const code = overrider?.fsrOfficerCode?.trim();
      return code ? `Overridden by ${code}` : 'Overridden by FSR';
    }
    case 'fail':
      return rollup.drivenBy
        ? `${labelForSection(rollup.drivenBy)} check failed — review required`
        : 'Verify check failed — review required';
  }
}

// ---------------------------------------------------------------------------
// Persistence shape — `person.extra.verifyClearance`
// ---------------------------------------------------------------------------

/**
 * Persisted shape on `person.extra.verifyClearance`. Each section key is
 * optional — absent = `pending` per `rollupVerifyState` default.
 */
export type VerifyClearanceMap = Partial<Record<VerifySection, SectionClearance>>;

/**
 * Lift the persisted map to a vector for `rollupVerifyState`.
 *
 * Bill 2026-06-12 (live smoke defect): absent sections MUST surface as
 * `pending`, not silently drop out — previously a map holding only
 * `email: pass` + `idScan: pass` rolled up "Verify: Cleared" while OFAC and
 * LexisNexis had never run. Every section is now represented, making the
 * documented "absent = pending" contract real. (Existing-CIF persons whose
 * prior-pass baseline skips LN/OFAC auto-run will honestly show Pending
 * until those sections carry a clearance — informational tone, not a block.)
 */
export function toClearanceVector(
  map: VerifyClearanceMap | undefined,
): ReadonlyArray<SectionClearance> {
  const out: SectionClearance[] = [];
  for (const section of ['ln', 'ofac', 'idScan', 'address', 'phone', 'email'] as const) {
    const row = map?.[section];
    out.push(row ?? markPending(section));
  }
  return out;
}

/** Merge a single section update into the persisted map. */
export function upsertSection(
  map: VerifyClearanceMap | undefined,
  next: SectionClearance,
): VerifyClearanceMap {
  return { ...(map ?? {}), [next.section]: next };
}
